import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React from "react";

import ButtonLink from "~/components/links/ButtonLink";
import IconLink from "~/components/links/IconLink";

import Col from "~/components/layout/Col";
import Row from "~/components/layout/Row";

import UnderlinedTitle from "~/components/typography/UnderlineTitle";
import { BreakContainer } from "~/components/ProductTable";

import { h2 } from "~/styles/typographyStyles";

import { sanitizeAndParseHTML } from "~/utilities/helpers";
import { isEmpty, isNil } from "lodash";

const Div = styled.div``;

const Wrapper = styled.div`
    border-bottom: ${({ theme, withBorder }) =>
        withBorder ? `3px solid ${theme.palette.purple}` : `none`};
    padding-bottom: ${({ withBorder }) => (withBorder ? `30px` : `0`)};
    margin-bottom: ${({ withBorder, marginBottom }) =>
        withBorder || marginBottom ? `30px` : `0`};
`;

const ProductSectionRow = styled(Row)`
    @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
        display: flex;
        flex-direction: column;
        align-items: baseline;
    }
`;

export const Title = styled.h2`
    ${h2};
    margin-bottom: 0;
    margin-right: 10px;
    font-weight: ${({ titleLightWeight }) =>
        titleLightWeight ? `500` : `700`};
    color: ${({ lightText, theme }) =>
        lightText ? theme.palette.white : theme.palette.black};
`;

const ModalTitle = Title.withComponent("h1");

const HeaderRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin: -15px -15px 0;
`;

const HeaderCol = styled.div`
    padding: 15px 15px 0;
`;

export const LINK_TYPES = ["button", "text"];

export const Copy = styled.div`
    margin-top: 10px;
    color: ${({ lightText, theme }) =>
        lightText ? theme.palette.white : theme.palette.black};
`;

const SectionHeading = ({
    copy = "",
    lightText,
    links = [],
    linkType = LINK_TYPES[1],
    productSection = false,
    titleLightWeight = false,
    smallTitle = false,
    title,
    withBorder = false,
    marginBottom = false,
    titleId = "",
    fullWidth = false,
    isModal = false,
}) => {
    const Container = fullWidth ? BreakContainer : Div;
    const SectionRow = productSection ? ProductSectionRow : Row;

    const ConditionalTitle = isModal ? ModalTitle : Title;

    links?.forEach(({ linkLabel, showViewAllLabel }, index) => {
        if (
            showViewAllLabel &&
            (isEmpty(linkLabel) ||
                isNil(linkLabel) ||
                linkLabel?.includes("View All"))
        ) {
            links[index].linkLabel = "View All";
        }
    });

    if (isEmpty(links) && isEmpty(title) && isEmpty(copy)) {
        return null;
    }

    return (
        <Container filterHeader={true}>
            <Wrapper {...{ withBorder, marginBottom }}>
                <SectionRow justifyContent="space-between" alignItems="center">
                    {title ? (
                        <Col xs="auto">
                            {smallTitle ? (
                                <UnderlinedTitle
                                    element="h2"
                                    smallMarginBottom
                                    {...{ titleId }}
                                >
                                    {sanitizeAndParseHTML(title)}
                                </UnderlinedTitle>
                            ) : (
                                <ConditionalTitle
                                    {...{ lightText, titleLightWeight }}
                                    id={titleId}
                                >
                                    {sanitizeAndParseHTML(title)}
                                </ConditionalTitle>
                            )}
                        </Col>
                    ) : null}
                    {links && links.length ? (
                        <Col xs="auto">
                            <HeaderRow>
                                {links.map(
                                    ({ linkLabel, linkUrl, linkAriaLabel }) => (
                                        <HeaderCol key={linkLabel}>
                                            {linkType === "button" ? (
                                                <ButtonLink
                                                    color={
                                                        lightText
                                                            ? "whiteAlt"
                                                            : "purple"
                                                    }
                                                    to={linkUrl}
                                                    ariaLabel={linkAriaLabel}
                                                >
                                                    {sanitizeAndParseHTML(
                                                        linkLabel,
                                                    )}
                                                </ButtonLink>
                                            ) : (
                                                <IconLink
                                                    to={linkUrl}
                                                    icon="chevron-right"
                                                    iconLast
                                                    color={
                                                        lightText
                                                            ? "whiteAlt"
                                                            : "purple"
                                                    }
                                                    aria-label={linkAriaLabel}
                                                >
                                                    {sanitizeAndParseHTML(
                                                        linkLabel,
                                                    )}
                                                </IconLink>
                                            )}
                                        </HeaderCol>
                                    ),
                                )}
                            </HeaderRow>
                        </Col>
                    ) : null}
                    {copy ? (
                        <Col xs={1} noVerticalSpacing>
                            <Copy {...{ lightText }}>
                                {sanitizeAndParseHTML(copy)}
                            </Copy>
                        </Col>
                    ) : null}
                </SectionRow>
            </Wrapper>
        </Container>
    );
};

SectionHeading.propTypes = {
    title: PropTypes.string,
    copy: PropTypes.string,
    links: PropTypes.arrayOf(
        PropTypes.shape({
            linkLabel: PropTypes.string,
            linkUrl: PropTypes.string,
            linkAriaLabel: PropTypes.string,
        }),
    ),
    linkType: PropTypes.oneOf(LINK_TYPES),
    productSection: PropTypes.bool,
    smallTitle: PropTypes.bool,
    withBorder: PropTypes.bool,
    marginBottom: PropTypes.bool,
    titleId: PropTypes.string,
    fullWidth: PropTypes.bool,
    titleLightWeight: PropTypes.bool,
};

export default SectionHeading;
